import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styles from '../styles.scss';
import { FormattedMessage } from 'react-intl';
import Spacer, { SpacerSize } from 'hellospa/foundation/spacing/spacer';
import { getContacts, selectReferAFriendUrl } from 'hellospa/page/prep-and-send/data/selectors';
import Checkbox from 'hellospa/foundation/control/checkbox';
import { getCSRFToken } from 'hellospa/hs-fetch';
import { Contact } from 'hellospa/page/prep-and-send/data/types';
import HelloModal from 'common/components/hello-modal';
import Button from 'hellospa/foundation/control/button';
import * as actions from 'hellospa/page/prep-and-send/data/actions';

type InviteFriendsProps = {
  onRequestClose: () => void,
  nextStep: () => void,
  isHelloFax: boolean
}
export function InviteFriends({
  onRequestClose,
  nextStep,
  isHelloFax,
}: InviteFriendsProps) {
  const dispatch = useDispatch();
  const [selectedContacts, setSelectedContacts] = React.useState<Contact[]>([]);
  const contacts = useSelector(getContacts);
  const referAFriendUrl = useSelector(selectReferAFriendUrl);

  const selectAll = () => setSelectedContacts(contacts);
  const selectNone = () => setSelectedContacts([]);


  const tellYourFriends = async () => {
    const emails = selectedContacts.map((c) => c.value);
    await dispatch(actions.inviteFriends(emails));
    nextStep();
  };

  React.useEffect(() => {
    if (!referAFriendUrl) {
      dispatch(actions.loadReferAFriendURL());
    }
  }, [dispatch, referAFriendUrl]);

  let inviteMsg;
  let marketingStatement;
  if (isHelloFax) {
    inviteMsg = (
      <FormattedMessage
        id=""
        defaultMessage="HelloFax is a web based service that lets you edit and sign documents and send them via email or fax.{br}Signup and we\'ll both get 5 Free fax pages!"
        values={{
          // eslint-disable-next-line react/display-name
          br: <br />,
        }}
        description="contact import page, inviting contacts form, predefined message to invitee" />
    );
    marketingStatement = (

      <FormattedMessage
        id=""
        defaultMessage="Want extra fax pages? You can earn five additional fax pages for each of your contacts who signs up for HelloFax. Use the buttons below select your contacts and send them an invitation to HelloFax with one click."
        description="contact import page, marketing statement, proposing to user to invite his/her friends and rewarding with free fax pages for everybody who signs up" />
    );
  } else {
    inviteMsg = (
      <React.Fragment>
        <FormattedMessage
          id=""
          defaultMessage="HelloSign is a free eSignature solution. It's simple and easy to sign documents or send signature requests.{br}Join me today, all e-signatures are totally free!{br}"

          values={{
            // eslint-disable-next-line react/display-name
            br: <br />,
          }}
          description="contact import page, inviting contacts form, predefined message to invitee" />
        <a data-qa-ref="refer-a-friend-link" href={referAFriendUrl}>{referAFriendUrl}</a>
      </React.Fragment>
    );
    marketingStatement = (
      <FormattedMessage
        id=""
        defaultMessage="Want to tell your your friends and colleagues about HelloSign? Use the buttons below to select your contacts and send them an invitation to HelloSign with one click."
        description="contact import page, marketing statement, proposing to user to invite his/her friends and rewarding with free documents for everybody who signs up" />
    );
  }

  return (
    <HelloModal
      isOpen={true}
      showCloseIcon={true}
      contentLabel={

        <FormattedMessage
          id=""
          defaultMessage="Contacts imported"
          description="contact import page. import form, title, informs user that contact importing finished" />
      }
      onRequestClose={onRequestClose}
      buttons={
        [
          <Button key="submit" onClick={tellYourFriends}>
            <FormattedMessage
              id=""
              defaultMessage="Tell your friends"
              description="contact import page, inviting contacts form, form submit button, send the invite message to selected contacts" />
          </Button>,
        ]
      }
    >

      <div>
        <Spacer below={SpacerSize.MD}>
          {marketingStatement}
        </Spacer>

        <form action="/account/contactImport" id="invite-contacts-form" method="POST">
          <input
            type="hidden"
            name="sendinviteemails[_csrf_token]"
            value={getCSRFToken()} />
          <Spacer className={styles.contactList} below={SpacerSize.SM} above={SpacerSize.SM}>
            {contacts
              .filter((c) => c.type === 'E')
              .map((contact) => (
                <Spacer below={SpacerSize.MS} className={styles.contact} key={contact.id}>
                  <Checkbox
                    id={`contact_${contact.id}`}
                    name="emails[]"
                    value={contact.value}
                    checked={selectedContacts.includes(contact)}
                    onChange={() => setSelectedContacts((selected) => {
                      if (selected.includes(contact)) {
                        return selected.filter((id) => id !== contact);
                      }
                      return [...selected, contact];
                    })} />
                  <label htmlFor={`contact_${contact.id}`}>
                    {contact.name} &lt;{contact.value}&gt;
                  </label>
                </Spacer>
              ))}
          </Spacer>

          <Spacer below={SpacerSize.MD}>
            <a href="#" onClick={selectAll} className={styles.selectContacts}>
              <FormattedMessage
                id=""
                defaultMessage='Check all'
                description="contact import page, inviting contacts form, mass contact selection button, selects all imported contacts" />
            </a>
          &nbsp;&nbsp;|&nbsp;&nbsp;
            <a href="#" onClick={selectNone} className={styles.selectContacts}>
              <FormattedMessage
                id=""
                defaultMessage="Uncheck all"
                description="contact import page, inviting contacts form, mass contact selection button, de-selects all imported contacts" />
            </a>
          </Spacer>
          <Spacer below={SpacerSize.SM}>
            <FormattedMessage
              id=""
              defaultMessage="Message"
              description="contact import page, inviting contacts form, title, label for field, message to invitee" />
          </Spacer>
          <Spacer className={styles.message} below={SpacerSize.MD}>
            {inviteMsg}
          </Spacer>
        </form>
      </div>
    </HelloModal>
  );
}
